import { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  ShoppingBag,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Maximize2,
  X,
  AlertCircle,
} from "lucide-react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { useToast } from "../context/ToastContext";
import * as productsApi from "../api/productsApi";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { formatMoney, formatProductSize } from "../utils/format";
import { resolveImageUrl } from "../utils/images";
import { useLanguage } from "../context/LanguageContext";
import { isGuid, resolveSlugToId, getProductSlug, registerProductSlug } from "../utils/productSlug";
import ProductRecommendations from "../components/ProductRecommendations";
import { updateSEO } from "../utils/seo";
import { FREE_SHIPPING_THRESHOLD } from "../constants/shipping";
import "./ProductDetail.css";

export default function ProductDetail({ onCartUpdated }) {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, userId } = useAuth();
  const { addToCart } = useCart();
  const { addToast } = useToast();
  const { t, isRTL, language } = useLanguage();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sizeId, setSizeId] = useState("");
  const [colorId, setColorId] = useState("");
  const [activeColorHasImage, setActiveColorHasImage] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const [adding, setAdding] = useState(false);
  const [addedFlash, setAddedFlash] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomPos, setZoomPos] = useState({ x: 50, y: 50 });
  const [showStickyBuy, setShowStickyBuy] = useState(false);
  const [touchStartX, setTouchStartX] = useState(null);
  const [touchStartY, setTouchStartY] = useState(null);
  const [touchEndX, setTouchEndX] = useState(null);
  const [touchEndY, setTouchEndY] = useState(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxScale, setLightboxScale] = useState(100);
  const [prevColorSrc, setPrevColorSrc] = useState(null);
  const [isColorFading, setIsColorFading] = useState(false);

  const heroRef = useRef(null);
  const thumbsRef = useRef(null);
  const lightboxThumbsRef = useRef(null);
  const mainBuyBtnRef = useRef(null);
  const transformRef = useRef(null);
  const isSwipingRef = useRef(false);
  const colorFadeTimeoutRef = useRef(null);

  // Auto-scroll active thumbnail into view in main gallery
  useEffect(() => {
    if (!thumbsRef.current) return;
    const activeEl = thumbsRef.current.querySelector(".pd-thumb--active");
    if (activeEl) {
      activeEl.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [activeImage, activeColorHasImage]);

  // Auto-scroll active thumbnail into view in lightbox
  useEffect(() => {
    if (!isLightboxOpen || !lightboxThumbsRef.current) return;
    const activeEl = lightboxThumbsRef.current.querySelector(".pd-lightbox__thumb--active");
    if (activeEl) {
      activeEl.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [activeImage, isLightboxOpen, activeColorHasImage]);

  // Clear any pending color fade timer on unmount
  useEffect(() => {
    return () => {
      if (colorFadeTimeoutRef.current) {
        clearTimeout(colorFadeTimeoutRef.current);
      }
    };
  }, []);

  // Preload color variant images for instant, flicker-free transitions
  useEffect(() => {
    if (Array.isArray(product?.colorVariants)) {
      product.colorVariants.forEach((c) => {
        if (c?.url) {
          const img = new Image();
          img.src = resolveImageUrl(c.url);
        }
      });
    }
  }, [product]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        let targetId = location.state?.productId;

        // If no targetId in navigation state, check if slug is already a GUID
        if (!targetId && isGuid(slug)) {
          targetId = slug;
        }

        // If still no targetId, resolve slug using cached mapping or catalog API
        if (!targetId) {
          targetId = await resolveSlugToId(slug, productsApi.fetchProducts);
        }

        if (!targetId) {
          throw new Error(t("product.notFound") || "Product not found.");
        }

        const p = await productsApi.fetchProductById(targetId);
        if (cancelled) return;
        registerProductSlug(p);
        setProduct(p);

        // Smoothly update address bar to canonical SEO slug if accessed via GUID or mismatched slug
        const canonicalSlug = getProductSlug(p);
        if (canonicalSlug && slug !== canonicalSlug) {
          window.history.replaceState(
            { ...window.history.state, productId: p.id },
            "",
            `/product/${canonicalSlug}`
          );
        }

        const sizes = p.sizeVariants || [];
        const colors = p.colorVariants || [];
        const stockItems = p.stock || p.Stock || [];

        // Check if there is an in-stock variant combination to pre-select
        const inStockCombo = stockItems.find((s) => Number(s.quantity ?? s.Quantity ?? 0) > 0);
        if (inStockCombo) {
          const matchedSizeId = inStockCombo.sizeId || inStockCombo.SizeId;
          const matchedColorId = inStockCombo.colorId || inStockCombo.ColorId;
          if (matchedSizeId && sizes.some((s) => s.id === matchedSizeId)) {
            setSizeId(matchedSizeId);
          } else if (sizes[0]?.id) {
            setSizeId(sizes[0].id);
          }
          if (matchedColorId && colors.some((c) => c.id === matchedColorId)) {
            setColorId(matchedColorId);
          } else if (colors[0]?.id) {
            setColorId(colors[0].id);
          }
        } else {
          if (sizes[0]?.id) setSizeId(sizes[0].id);
          if (colors[0]?.id) setColorId(colors[0].id);
        }
      } catch (e) {
        if (!cancelled) addToast(e.message || "Product not found.", "error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, location.state?.productId, addToast, t]);

  // Set page title, meta description & Schema.org JSON-LD for SEO & AI search
  useEffect(() => {
    if (product) {
      const titleName = isRTL && product.arabicName ? product.arabicName : product.name;
      const descText =
        (isRTL && product.arabicDescription ? product.arabicDescription : product.description) ||
        (isRTL
          ? "منتج فاخر من تيري لاند بخامات عالية الجودة تجمع بين القطن والمايكروفايبر والوجهين المزدوجة."
          : "Luxury textile product by Terry Land crafted from high-quality cotton & microfiber.");
      
      const primaryImage =
        product.thumbnailImage ||
        (product.productImages && product.productImages[0]?.url) ||
        "https://terryland.net/logo512.png";

      const canonicalUrl = `https://terryland.net/product/${slug}`;

      const productSchema = {
        "@context": "https://schema.org/",
        "@type": "Product",
        "name": titleName,
        "image": [primaryImage.startsWith("http") ? primaryImage : `https://terryland.net${primaryImage}`],
        "description": descText,
        "brand": {
          "@type": "Brand",
          "name": "تيري لاند | Terry Land"
        },
        "offers": {
          "@type": "Offer",
          "url": canonicalUrl,
          "priceCurrency": "EGP",
          "price": Number(product.basePrice) || 0,
          "availability": "https://schema.org/InStock",
          "itemCondition": "https://schema.org/NewCondition"
        }
      };

      updateSEO({
        title: `${titleName} | ${isRTL ? "تيري لاند" : "Terry Land"}`,
        description: descText.slice(0, 160),
        canonical: canonicalUrl,
        ogType: "product",
        ogImage: primaryImage.startsWith("http") ? primaryImage : `https://terryland.net${primaryImage}`,
        jsonLd: productSchema,
      });
    }
  }, [product, isRTL, slug]);

  // Observe main action buttons to toggle mobile sticky buy bar
  useEffect(() => {
    const target = mainBuyBtnRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // When the primary buy button is scrolled out of view, show sticky bar
        setShowStickyBuy(!entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [loading, product]);

  const images = (product?.productImages || [])
    .slice()
    .sort((a, b) => (Number(a.displayOrder) || 0) - (Number(b.displayOrder) || 0));

  // Horizontal mouse-wheel scroll on desktop for thumbnails
  useEffect(() => {
    const el = thumbsRef.current;
    if (!el) return;

    const onWheel = (e) => {
      if (el.scrollWidth > el.clientWidth) {
        if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
          e.preventDefault();
          el.scrollBy({
            left: e.deltaY * 1.2,
            behavior: "smooth",
          });
        }
      }
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [images.length]);

  const handleThumbsScroll = (direction) => {
    if (!thumbsRef.current) return;
    const step = 200;
    const factor = direction === "next" ? (isRTL ? -1 : 1) : (isRTL ? 1 : -1);
    thumbsRef.current.scrollBy({
      left: step * factor,
      behavior: "smooth",
    });
  };

  const handleMouseEnter = () => {
    // Only enable hover zoom on desktop fine pointers
    if (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
      setIsZoomed(true);
    }
  };

  const handleMouseMove = (e) => {
    if (!isZoomed) return;
    const rect = heroRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomPos({ x, y });
  };

  const handlePrevImage = () => {
    if (images.length <= 1) return;
    setActiveColorHasImage(false);
    setActiveImage((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const handleNextImage = () => {
    if (images.length <= 1) return;
    setActiveColorHasImage(false);
    setActiveImage((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  };

  // Lock scroll and handle keyboard shortcuts when lightbox is open
  useEffect(() => {
    if (!isLightboxOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsLightboxOpen(false);
      } else if (e.key === "ArrowLeft") {
        if (isRTL) handleNextImage();
        else handlePrevImage();
        if (transformRef.current) transformRef.current.resetTransform();
      } else if (e.key === "ArrowRight") {
        if (isRTL) handlePrevImage();
        else handleNextImage();
        if (transformRef.current) transformRef.current.resetTransform();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isLightboxOpen, isRTL, images.length]);

  const handleLightboxPrev = () => {
    handlePrevImage();
    if (transformRef.current) transformRef.current.resetTransform();
  };

  const handleLightboxNext = () => {
    handleNextImage();
    if (transformRef.current) transformRef.current.resetTransform();
  };

  const handleLightboxSelectImage = (index) => {
    setActiveColorHasImage(false);
    setActiveImage(index);
    if (transformRef.current) transformRef.current.resetTransform();
  };

  // Mobile Touch Swipe Handlers
  const handleTouchStart = (e) => {
    isSwipingRef.current = false;
    setTouchEndX(null);
    setTouchEndY(null);
    setTouchStartX(e.targetTouches[0].clientX);
    setTouchStartY(e.targetTouches[0].clientY);
  };

  const handleTouchMove = (e) => {
    const currentX = e.targetTouches[0].clientX;
    const currentY = e.targetTouches[0].clientY;
    setTouchEndX(currentX);
    setTouchEndY(currentY);

    if (touchStartX !== null && touchStartY !== null) {
      const diffX = Math.abs(currentX - touchStartX);
      const diffY = Math.abs(currentY - touchStartY);
      if (diffX > diffY && diffX > 10) {
        isSwipingRef.current = true;
      }
    }
  };

  const handleTouchEnd = () => {
    if (touchStartX === null || touchEndX === null) return;
    const diffX = touchStartX - touchEndX;
    const diffY = (touchStartY ?? 0) - (touchEndY ?? 0);
    const minSwipe = 35;

    // Only swipe if horizontal distance exceeds minSwipe and is greater than vertical movement
    if (Math.abs(diffX) > minSwipe && Math.abs(diffX) > Math.abs(diffY)) {
      if (isRTL) {
        // In RTL: swipe left (diffX > 0) goes to previous, swipe right (diffX < 0) goes to next
        if (diffX > 0) handlePrevImage();
        else handleNextImage();
      } else {
        // In LTR: swipe left (diffX > 0) goes to next, swipe right (diffX < 0) goes to previous
        if (diffX > 0) handleNextImage();
        else handlePrevImage();
      }
    }
    setTouchStartX(null);
    setTouchStartY(null);
    setTouchEndX(null);
    setTouchEndY(null);
  };

  const handleColorSelect = (c) => {
    if (c.id === colorId) return;

    const nextUrl = c.url ? resolveImageUrl(c.url) : null;
    const activeCol = (product?.colorVariants || []).find((col) => col.id === colorId);
    const currentUrl = (activeColorHasImage && activeCol?.url)
      ? resolveImageUrl(activeCol.url)
      : (images[activeImage]?.url ? resolveImageUrl(images[activeImage].url) : null);

    if (currentUrl && nextUrl && currentUrl !== nextUrl) {
      if (colorFadeTimeoutRef.current) {
        clearTimeout(colorFadeTimeoutRef.current);
      }
      setPrevColorSrc(currentUrl);
      setIsColorFading(true);

      colorFadeTimeoutRef.current = setTimeout(() => {
        setIsColorFading(false);
        setPrevColorSrc(null);
      }, 260);
    }

    setColorId(c.id);
    setActiveColorHasImage(!!c.url);

    // On mobile screens, smoothly scroll back up to the hero image to show the selected color
    if (typeof window !== "undefined" && window.innerWidth <= 860) {
      if (heroRef.current) {
        const navbarEl = document.querySelector(".navbar");
        const navHeight = navbarEl ? navbarEl.offsetHeight : 72;
        const navOffset = navHeight + 12;
        const elementPosition = heroRef.current.getBoundingClientRect().top;
        const targetScrollTop = elementPosition + window.scrollY - navOffset;

        window.scrollTo({
          top: Math.max(0, targetScrollTop),
          behavior: "smooth",
        });
      }
    }
  };

  const displayName = isRTL && product?.arabicName ? product.arabicName : product?.name;
  const displayDesc = isRTL && product?.arabicDescription ? product.arabicDescription : product?.description;

  const handleAddToCart = async () => {
    if (!sizeId || !colorId) {
      addToast(t("product.selectColorAndSize"), "error");
      return;
    }

    if (isOutOfStock) {
      addToast(t("product.selectCombinationOutOfStock"), "error");
      return;
    }

    setAdding(true);
    try {
      const res = await addToCart({
        productId: product.id,
        name: displayName,
        price: Number(product.basePrice),
        quantity: 1,
        length: Number(product.length) || 0,
        width: Number(product.width) || 0,
        height: Number(product.height) || 0,
        sizeVariantId: sizeId,
        sizeName: selectedSize?.size,
        colorVariantId: colorId,
        colorName: selectedColor?.color,
        colorHex: selectedColor?.colorHex,
        imageUrl: selectedColor?.url || product.thumbnailImage || (images[0]?.url),
      });

      if (res && res.success === false) {
        return;
      }

      onCartUpdated && onCartUpdated();
      setAddedFlash(true);
      setTimeout(() => setAddedFlash(false), 2000);
      addToast(t("action.added"), "success");
    } catch (e) {
      addToast(e.message || "Could not add to cart.", "error");
    } finally {
      setAdding(false);
    }
  };

  if (loading) {
    return (
      <div className="pd-page">
        <div className="pd-skeleton">
          <div className="pd-skeleton__gallery">
            <div className="pd-skeleton__thumbs">
              {[1,2,3,4].map(i => <div key={i} className="pd-skeleton__thumb-item shimmer" />)}
            </div>
            <div className="pd-skeleton__hero shimmer" />
          </div>
          <div className="pd-skeleton__info">
            <div className="pd-skeleton__line shimmer" style={{ width: "40%", height: 14 }} />
            <div className="pd-skeleton__line shimmer" style={{ width: "80%", height: 36, marginTop: 12 }} />
            <div className="pd-skeleton__line shimmer" style={{ width: "30%", height: 28, marginTop: 16 }} />
            <div className="pd-skeleton__line shimmer" style={{ width: "100%", height: 80, marginTop: 28 }} />
            <div className="pd-skeleton__line shimmer" style={{ width: "60%", height: 48, marginTop: 36 }} />
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="pd-page">
        <div className="container" style={{ paddingTop: 80, textAlign: "center" }}>
          <p style={{ marginBottom: 24, color: "var(--text-muted)" }}>{t("product.notFound")}</p>
          <Link to="/shop" className="btn-primary">{t("action.backToShop")}</Link>
        </div>
      </div>
    );
  }

  const sizes = product.sizeVariants || [];
  const colors = product.colorVariants || [];
  const selectedColor = colors.find(c => c.id === colorId);
  const selectedSize = sizes.find(s => s.id === sizeId);

  const stockList = Array.isArray(product.stock)
    ? product.stock
    : Array.isArray(product.Stock)
    ? product.Stock
    : [];

  const getVariantStock = (sId, cId) => {
    if (!sId || !cId) return 0;
    const match = stockList.find(
      (s) =>
        String(s.sizeId || s.SizeId || "").toLowerCase() === String(sId).toLowerCase() &&
        String(s.colorId || s.ColorId || "").toLowerCase() === String(cId).toLowerCase()
    );
    return match ? Number(match.quantity ?? match.Quantity ?? 0) : 0;
  };

  const currentStock = getVariantStock(sizeId, colorId);
  const isOutOfStock = currentStock <= 0;
  const isLowStock = currentStock > 0 && currentStock <= 3;

  const activeColorObj = colors.find(c => c.id === colorId);
  const heroSrc = (activeColorHasImage && activeColorObj?.url)
    ? resolveImageUrl(activeColorObj.url)
    : (images[activeImage]?.url ? resolveImageUrl(images[activeImage].url) : null);

  return (
    <div className="pd-page">
      {/* Breadcrumb */}
      <div className="pd-breadcrumb">
        <div className="container">
          <Link to="/" className="pd-breadcrumb__link">{t("nav.home")}</Link>
          <span className="pd-breadcrumb__sep">/</span>
          <Link to="/shop" className="pd-breadcrumb__link">{t("nav.shop")}</Link>
          <span className="pd-breadcrumb__sep">/</span>
          <span className="pd-breadcrumb__current">{displayName}</span>
        </div>
      </div>

      <div className="container pd-inner">
        <div className="pd-grid">

          {/* ── GALLERY ── */}
          <div className="pd-gallery">
            {/* Main hero image */}
            <div
              ref={heroRef}
              className={`pd-hero ${isZoomed ? "pd-hero--zoomed" : ""}`}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={() => setIsZoomed(false)}
              onMouseMove={handleMouseMove}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onClick={() => {
                if (isSwipingRef.current) {
                  isSwipingRef.current = false;
                  return;
                }
                setIsLightboxOpen(true);
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setIsLightboxOpen(true);
                }
              }}
              aria-label={displayName || "Product image"}
            >
              {/* Sliding Track for images or single activeColor image */}
              {activeColorHasImage && activeColorObj?.url ? (
                <div className="pd-hero__slide pd-hero__slide--color">
                  {prevColorSrc && isColorFading && (
                    <img
                      src={prevColorSrc}
                      alt=""
                      className="pd-hero__img pd-hero__img--prev"
                      draggable={false}
                      aria-hidden="true"
                    />
                  )}
                  <img
                    key={activeColorObj.url}
                    src={resolveImageUrl(activeColorObj.url)}
                    alt={`${displayName} - ${selectedColor?.color || ""}`}
                    className={`pd-hero__img ${isColorFading ? "pd-hero__img--fading-in" : ""}`}
                    draggable={false}
                  />
                </div>
              ) : images.length > 0 ? (
                <div
                  className="pd-hero__slider-track"
                  style={{
                    transform: `translateX(${isRTL ? activeImage * 100 : -activeImage * 100}%)`,
                  }}
                >
                  {images.map((im, idx) => (
                    <div key={im.id || idx} className="pd-hero__slide">
                      <img
                        src={resolveImageUrl(im.url)}
                        alt={`${displayName} - ${idx + 1}`}
                        className="pd-hero__img"
                        draggable={false}
                        loading={idx === 0 ? "eager" : "lazy"}
                      />
                    </div>
                  ))}
                </div>
              ) : heroSrc ? (
                <div className="pd-hero__slide pd-hero__slide--color">
                  {prevColorSrc && isColorFading && (
                    <img
                      src={prevColorSrc}
                      alt=""
                      className="pd-hero__img pd-hero__img--prev"
                      draggable={false}
                      aria-hidden="true"
                    />
                  )}
                  <img
                    key={heroSrc}
                    src={heroSrc}
                    alt={displayName}
                    className={`pd-hero__img ${isColorFading ? "pd-hero__img--fading-in" : ""}`}
                    draggable={false}
                  />
                </div>
              ) : null}

              {/* Zoom overlay on desktop hover */}
              {isZoomed && heroSrc && (
                <div
                  className="pd-hero__zoom"
                  style={{
                    backgroundImage: `url(${heroSrc})`,
                    backgroundPosition: `${zoomPos.x}% ${zoomPos.y}%`,
                  }}
                />
              )}

              {/* Navigation arrows for touch & mobile */}
              {images.length > 1 && (
                <>
                  <button
                    type="button"
                    className="pd-hero__nav pd-hero__nav--prev"
                    onClick={(e) => { e.stopPropagation(); isRTL ? handleNextImage() : handlePrevImage(); }}
                    aria-label="Previous image"
                  >
                    <ChevronLeft size={18} className="rtl-flip" />
                  </button>
                  <button
                    type="button"
                    className="pd-hero__nav pd-hero__nav--next"
                    onClick={(e) => { e.stopPropagation(); isRTL ? handlePrevImage() : handleNextImage(); }}
                    aria-label="Next image"
                  >
                    <ChevronRight size={18} className="rtl-flip" />
                  </button>
                  <div className="pd-hero__counter" aria-label={`Image ${activeImage + 1} of ${images.length}`}>
                    <span>{activeImage + 1}</span>
                    <span className="pd-hero__counter-sep">/</span>
                    <span>{images.length}</span>
                  </div>
                </>
              )}
            </div>

            {/* Thumbnail rail underneath main hero image */}
            {images.length > 1 && (
              <div className="pd-thumbs-container">
                {images.length > 5 && (
                  <button
                    type="button"
                    className="pd-thumbs-arrow pd-thumbs-arrow--prev"
                    onClick={() => handleThumbsScroll("prev")}
                    aria-label={isRTL ? "الصور السابقة" : "Previous images"}
                    title={isRTL ? "السابق" : "Previous"}
                  >
                    <ChevronLeft size={16} className="rtl-flip" />
                  </button>
                )}

                <div
                  className={`pd-thumbs ${images.length <= 5 ? "pd-thumbs--centered" : ""}`}
                  ref={thumbsRef}
                >
                  {images.map((im, i) => {
                    const src = resolveImageUrl(im.url);
                    const isActive = activeImage === i && !activeColorHasImage;
                    return (
                      <button
                        key={im.id || i}
                        type="button"
                        className={`pd-thumb ${isActive ? "pd-thumb--active" : ""}`}
                        onClick={() => { setActiveImage(i); setActiveColorHasImage(false); }}
                        aria-label={`View image ${i + 1}`}
                      >
                        <span
                          className="pd-thumb__img"
                          style={src ? { backgroundImage: `url(${src})` } : {}}
                        />
                      </button>
                    );
                  })}
                </div>

                {images.length > 5 && (
                  <button
                    type="button"
                    className="pd-thumbs-arrow pd-thumbs-arrow--next"
                    onClick={() => handleThumbsScroll("next")}
                    aria-label={isRTL ? "الصور التالية" : "Next images"}
                    title={isRTL ? "التالي" : "Next"}
                  >
                    <ChevronRight size={16} className="rtl-flip" />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* ── PRODUCT INFO ── */}
          <div className="pd-info">
            <div className="pd-info__brand">{t("brand.name")}</div>
            <h1 className="pd-info__name">{displayName}</h1>

            <div className="pd-info__price-row">
              <span className="pd-info__price">{formatMoney(product.basePrice, language)}</span>
              <span className="pd-info__price-note">
                {t("product.freeDeliveryNote").replace("{amount}", formatMoney(FREE_SHIPPING_THRESHOLD, language))}
              </span>
            </div>

            <div className="pd-info__divider" />

            <p className="pd-info__desc">{displayDesc}</p>

            <div className="pd-info__divider" />

            {/* Color Selector */}
            {colors.length > 0 && (
              <div className="pd-selector">
                <div className="pd-selector__label">
                  <span>{t("product.color")}</span>
                  <span className="pd-selector__value" key={selectedColor?.color}>{selectedColor?.color || "—"}</span>
                </div>
                <div className="pd-colors">
                  {colors.map(c => {
                    const isSelected = colorId === c.id;
                    const hex = c.colorHex
                      ? (c.colorHex.startsWith("#") ? c.colorHex : `#${c.colorHex}`)
                      : "#cccccc";
                    const colorStock = getVariantStock(sizeId, c.id);
                    const isColorOOS = colorStock <= 0;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        className={`pd-color ${isSelected ? "pd-color--selected" : ""} ${isColorOOS ? "pd-color--oos" : ""}`}
                        title={isColorOOS ? `${c.color} (${t("product.outOfStock")})` : c.color}
                        aria-label={`Select color ${c.color}${isColorOOS ? ` - ${t("product.outOfStock")}` : ""}`}
                        onClick={() => handleColorSelect(c)}
                      >
                        <span className="pd-color__dot" style={{ backgroundColor: hex }} />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Size Selector */}
            {sizes.length > 0 && (
              <div className="pd-selector">
                <div className="pd-selector__label">
                  <span>{t("product.size")}</span>
                  <span className="pd-selector__value">{selectedSize ? formatProductSize(selectedSize.size, language) : "—"}</span>
                </div>
                <div className="pd-sizes">
                  {sizes.map(s => {
                    const isSelected = sizeId === s.id;
                    const sizeStock = getVariantStock(s.id, colorId);
                    const isSizeOOS = sizeStock <= 0;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        className={`pd-size ${isSelected ? "pd-size--selected" : ""} ${isSizeOOS ? "pd-size--oos" : ""}`}
                        title={isSizeOOS ? `${formatProductSize(s.size, language)} (${t("product.outOfStock")})` : formatProductSize(s.size, language)}
                        onClick={() => setSizeId(s.id)}
                      >
                        {formatProductSize(s.size, language)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Stock Availability Badge */}
            <div className="pd-stock-status">
              {isOutOfStock ? (
                <div className="pd-stock-badge pd-stock-badge--oos" role="status">
                  <span className="pd-stock-badge__dot" />
                  <span>{t("product.outOfStock")}</span>
                </div>
              ) : isLowStock ? (
                <div className="pd-stock-badge pd-stock-badge--low" role="status">
                  <span className="pd-stock-badge__dot" />
                  <span>{t("product.onlyLeft").replace("{count}", currentStock)}</span>
                </div>
              ) : (
                <div className="pd-stock-badge pd-stock-badge--instock" role="status">
                  <span className="pd-stock-badge__dot" />
                  <span>{t("product.inStock")}</span>
                </div>
              )}
            </div>

            <div className="pd-info__divider" />

            {/* Actions */}
            <div className="pd-actions" ref={mainBuyBtnRef}>
              <button
                type="button"
                className={`pd-btn-add ${
                  isOutOfStock
                    ? "pd-btn-add--oos"
                    : addedFlash
                    ? "pd-btn-add--success"
                    : ""
                }`}
                onClick={handleAddToCart}
                disabled={adding || isOutOfStock || sizes.length === 0 || colors.length === 0}
                aria-disabled={adding || isOutOfStock}
              >
                {isOutOfStock ? (
                  <>
                    <AlertCircle size={18} />
                    <span>{t("product.outOfStock")}</span>
                  </>
                ) : (
                  <>
                    <ShoppingBag size={18} />
                    <span>{adding ? t("action.adding") : addedFlash ? t("action.added") : t("action.addToCart")}</span>
                  </>
                )}
              </button>
              <Link to="/cart" className="pd-btn-cart">
                {t("action.viewCart")}
              </Link>
            </div>

            {isOutOfStock && (
              <div className="pd-stock-hint" role="alert">
                <AlertCircle size={15} />
                <span>{t("product.selectCombinationOutOfStock")}</span>
              </div>
            )}

            {!isAuthenticated && (
              <p className="pd-info__auth-note">
                {t("product.guestNotice")} <Link to="/login">{t("product.signInLink")}</Link> {t("product.saveBagNote")}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Dynamic Related & Recommended Products Carousel */}
      {product && (
        <ProductRecommendations
          currentProduct={product}
          onAddToCart={handleAddToCart}
        />
      )}

      {/* Mobile Sticky Buy Bar */}
      {showStickyBuy && (
        <aside className="pd-sticky-buy" aria-label="Quick Add to Bag">
          <div className="container pd-sticky-buy__inner">
            <div className="pd-sticky-buy__product">
              {heroSrc && (
                <div
                  className="pd-sticky-buy__thumb"
                  style={{ backgroundImage: `url(${heroSrc})` }}
                />
              )}
              <div className="pd-sticky-buy__meta">
                <span className="pd-sticky-buy__name">{displayName}</span>
                <span className="pd-sticky-buy__price">{formatMoney(product.basePrice, language)}</span>
              </div>
            </div>
            <button
              type="button"
              className={`pd-sticky-buy__btn ${
                isOutOfStock
                  ? "pd-sticky-buy__btn--oos"
                  : addedFlash
                  ? "pd-sticky-buy__btn--success"
                  : ""
              }`}
              onClick={handleAddToCart}
              disabled={adding || isOutOfStock || sizes.length === 0 || colors.length === 0}
              aria-disabled={adding || isOutOfStock}
            >
              {isOutOfStock ? (
                <>
                  <AlertCircle size={16} />
                  <span>{t("product.outOfStock")}</span>
                </>
              ) : (
                <>
                  <ShoppingBag size={16} />
                  <span>{adding ? t("action.adding") : addedFlash ? t("action.added") : t("action.addToCart")}</span>
                </>
              )}
            </button>
          </div>
        </aside>
      )}

      {/* ── IMAGE INSPECTOR LIGHTBOX MODAL ── */}
      {isLightboxOpen && heroSrc && (
        <div
          className="pd-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={displayName || "Image Inspector"}
        >
          <div
            className="pd-lightbox__backdrop"
            onClick={() => setIsLightboxOpen(false)}
          />

          {/* Top Bar */}
          <div className="pd-lightbox__header">
            <div className="pd-lightbox__meta">
              <span className="pd-lightbox__title">{displayName}</span>
              {images.length > 1 && (
                <span className="pd-lightbox__counter">
                  {t("product.imageCount")
                    .replace("{current}", activeImage + 1)
                    .replace("{total}", images.length)}
                </span>
              )}
            </div>
            <button
              type="button"
              className="pd-lightbox__close"
              onClick={() => setIsLightboxOpen(false)}
              aria-label={t("product.closeInspector")}
              title={t("product.closeInspector")}
            >
              <X size={22} />
            </button>
          </div>

          {/* Main Stage with Zoom / Pan / Pinch */}
          <div className="pd-lightbox__stage">
            <TransformWrapper
              ref={transformRef}
              initialScale={1}
              minScale={0.8}
              maxScale={4}
              centerOnInit={true}
              wheel={{ step: 0.15 }}
              pinch={{ step: 4 }}
              doubleClick={{ mode: "toggle", step: 2 }}
              panning={{ velocityDisabled: false }}
              onTransformed={(ref) => {
                const s = ref?.state?.scale ?? ref?.instance?.transformState?.scale;
                if (typeof s === "number" && !isNaN(s)) {
                  setLightboxScale(Math.round(s * 100));
                }
              }}
            >
              {({ zoomIn, zoomOut, resetTransform, state, instance }) => {
                const rawScale = state?.scale ?? instance?.transformState?.scale ?? (lightboxScale / 100);
                const currentScale = typeof rawScale === "number" && !isNaN(rawScale) ? rawScale : 1;

                return (
                  <>
                    <TransformComponent
                      wrapperStyle={{ width: "100%", height: "100%" }}
                      contentStyle={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src={heroSrc}
                        alt={displayName}
                        className="pd-lightbox__img"
                        draggable={false}
                      />
                    </TransformComponent>

                    {/* Floating Glass Zoom Controls Pill */}
                    <div className="pd-lightbox__controls">
                      <button
                        type="button"
                        className="pd-lightbox__ctrl-btn"
                        onClick={() => zoomOut(0.35)}
                        disabled={currentScale <= 0.8}
                        aria-label={t("product.zoomOut")}
                        title={t("product.zoomOut")}
                      >
                        <ZoomOut size={18} />
                      </button>
                      <button
                        type="button"
                        className="pd-lightbox__ctrl-btn"
                        onClick={() => zoomIn(0.35)}
                        disabled={currentScale >= 4}
                        aria-label={t("product.zoomIn")}
                        title={t("product.zoomIn")}
                      >
                        <ZoomIn size={18} />
                      </button>
                      <button
                        type="button"
                        className="pd-lightbox__ctrl-btn pd-lightbox__ctrl-btn--reset"
                        onClick={() => resetTransform()}
                        aria-label={t("product.resetZoom")}
                        title={t("product.resetZoom")}
                      >
                        <RotateCcw size={16} />
                      </button>
                    </div>
                  </>
                );
              }}
            </TransformWrapper>

            {/* Left / Right Gallery Navigation Buttons */}
            {images.length > 1 && (
              <>
                <button
                  type="button"
                  className="pd-lightbox__nav pd-lightbox__nav--prev"
                  onClick={(e) => {
                    e.stopPropagation();
                    isRTL ? handleLightboxNext() : handleLightboxPrev();
                  }}
                  aria-label="Previous image"
                >
                  <ChevronLeft size={28} className="rtl-flip" />
                </button>
                <button
                  type="button"
                  className="pd-lightbox__nav pd-lightbox__nav--next"
                  onClick={(e) => {
                    e.stopPropagation();
                    isRTL ? handleLightboxPrev() : handleLightboxNext();
                  }}
                  aria-label="Next image"
                >
                  <ChevronRight size={28} className="rtl-flip" />
                </button>
              </>
            )}
          </div>

          {/* Bottom Thumbnails Strip */}
          {images.length > 1 && (
            <div className="pd-lightbox__thumbs" ref={lightboxThumbsRef}>
              {images.map((im, i) => {
                const src = resolveImageUrl(im.url);
                const isActive = activeImage === i && !activeColorHasImage;
                return (
                  <button
                    key={im.id || i}
                    type="button"
                    className={`pd-lightbox__thumb ${isActive ? "pd-lightbox__thumb--active" : ""}`}
                    onClick={() => handleLightboxSelectImage(i)}
                    aria-label={`View image ${i + 1}`}
                  >
                    <span
                      className="pd-lightbox__thumb-img"
                      style={src ? { backgroundImage: `url(${src})` } : {}}
                    />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
